import { Pagination } from "@/interfaces/Pagination"
import { useAxios } from "../useAxios"
import { useBot } from "../useBot"
import { FlowStatusEnum } from "@/enums/chatFlow/flows/FlowStatusEnum"
import { Flow } from "@/interfaces/chat-flow/flow/Flow"
import { ref } from "vue"

const axios = useAxios()

export interface CreateFlowDTO {
  name: string
  description: string
  leadStartParameterContent?: string
  status: FlowStatusEnum
  allowChatResume?: boolean
}

export interface UpdateFlowDTO {
  name?: string
  url?: string
  status?: FlowStatusEnum
}

export interface GetAllFlowsParams {
  page?: string
  perPage?: string
}

export interface FlowSummary {
  paidOrdersCount: number,
  totalCommission: number,
  upsellCount: number,
  downsellCount: number,
  leadsCount: number
}

export const flows = ref<Pagination<Flow>>()

export function useFlow() {
  const { selectedBot } = useBot()

  const create = async (payload: CreateFlowDTO): Promise<Flow> => {
    const response = await axios.post(`/bots/${selectedBot.value?.id}/flows`, payload)

    return response.data
  }

  const update = async (id: number, payload: UpdateFlowDTO): Promise<void> => {
    await axios.patch(`/bots/${selectedBot.value?.id}/flows/${id}`, payload)
  }

  const getAll = async (params?: GetAllFlowsParams): Promise<Pagination<Flow>> => {
    if (params) {
      params = Object.fromEntries(
        Object.entries(params).filter(([param, value]) => !!value)
      )
    }

    if (selectedBot.value?.id) {
      const response = await axios.get(`/bots/${selectedBot.value?.id}/flows`, { params })
  
      return response.data
    } else {
      return { data: [], pagination: { currentPage: 1, perPage: 10, total: 0, from: 0, lastPage: 0, nextPage: 0, to: 0 } }
    }
  }

  const getAllOrgWide = async (params?: GetAllFlowsParams): Promise<Pagination<Flow>> => {
    if (params) {
      params = Object.fromEntries(
        Object.entries(params).filter(([param, value]) => !!value)
      )
    }

    const response = await axios.get('/bots/flows', { params })

    return response.data
  }

  const getById = async (id: number): Promise<Flow> => {
    const response = await axios.get(`/bots/${selectedBot.value?.id}/flows/${id}`)
    return response.data
  }

  const destroy = async (id: number): Promise<void> => {
    await axios.delete(`/bots/${selectedBot.value?.id}/flows/${id}`)
  }

  const getSummary = async (id: number): Promise<FlowSummary> => {
    const response = await axios.get(`/bots/${selectedBot.value?.id}/flows/${id}/summary`)

    return response.data
  }

  return {
    create,
    getAll,
    getAllOrgWide,
    getById,
    update,
    destroy,
    getSummary
  }
}